/* eslint-disable @typescript-eslint/no-explicit-any */
import { Checkbox, Heading, Link, UnorderedList, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { registerUser, validatePromocode, vinculateCPF, vinculateExternalCode } from '@partiu/api/auth/tokens';
import { setAccessToken } from '@partiu/api/axios';
import { IPlan } from '@partiu/api/dashboard/types';
import { useAPICallAsMutation } from '@partiu/api/hooks';
import { ICodeCheckResponse, IUserRegistrationData } from '@partiu/api/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../../../packages/ui/components/atoms/Button';
import { ExternalLink } from '../../../../../../packages/ui/components/atoms/ExternalLink';
import {
  CellphoneInput,
  CPFCNPJInput,
  Input,
  PasswordInput,
} from '../../../../../../packages/ui/components/atoms/Input';
import { TermsOfUseNotification } from '../../../../../../packages/ui/components/atoms/TermsOfUseNotification';
import { Labeled } from '../../../../../../packages/ui/components/molecules/Labeled';
import { useProfile } from '../../../../../../packages/ui/contexts/ProfileProvider';
import { useTheme } from '../../../../../../packages/ui/contexts/ThemeProvider';
import { ModalCPF } from '@partiu/api/components/ModalCPF';
import { masks } from '../../../../../../packages/ui/masks';

export function RegisterForm() {
  const toast = useToast();
  const { id, cpfModule, allowPromocodeLoginMethod, metadata } = useTheme();
  const { updateProfile } = useProfile();
  const navigate = useNavigate();
  const modalCpf = useDisclosure();
  const [registerType, setRegisterType] = useState<'promocode' | 'cpf' | 'subscription' | 'externalCode' | null>(null);
  const [accessCode, setAccessCode] = useState<string>('');
  const [accessCodeValidated, setAccessCodeValidated] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { register, handleSubmit, setValue, watch } = useForm();
  const [activatedPlan, setActivatedPlan] = useState<IPlan | null>(null);

  const accessCodeLabel = useMemo(() => {
    if (registerType === 'promocode') return 'Código Promocional';
    else if (registerType === 'cpf') return 'CPF / CNPJ';

    return metadata?.labelsubscriptionexternalcode || 'Código de Acesso';
  }, [registerType]);

  const validatorFunc = useCallback(async () => {
    if (!id) return false;

    if (registerType === 'externalCode') {
      return vinculateExternalCode(accessCode, id);
    }

    if (registerType === 'promocode') return validatePromocode(accessCode);
    else if (registerType === 'cpf') {
      return vinculateCPF(accessCode, id);
    }
  }, [registerType, id, accessCode]);

  const validatorMutation = useMutation(() => validatorFunc() as Promise<ICodeCheckResponse>, {
    onSuccess(data: any) {
      setAccessCodeValidated(true);
      if (data?.first_signup && data?.is_cnpj) {
        modalCpf.onOpen();
      }
      if (data?.is_cnpj && data?.document_for_cnpj) {
        setValue('cpf', masks.cpf(data?.document_for_cnpj));
      }
      if (data.plan) setActivatedPlan(data.plan);

      if (registerType === 'externalCode' && metadata?.masksubscriptionexternalcode === 'phone') {
        setValue('telephone', masks.cell(accessCode));
      }
    },
    onError(err: any) {
      let errorMessage = `Parece que este ${accessCodeLabel} não é valido. Por favor verifique se está correto.`;
      if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      toast({
        title: 'Oops!',
        description: errorMessage,
        status: 'error',
      });
    },
  });

  const registerMutation = useAPICallAsMutation(
    (data: IUserRegistrationData) => {
      if (registerType === 'promocode') {
        data.promocode = accessCode || undefined;
      }
      delete data.accessCode;
      if (registerType === 'externalCode') {
        data.external_code = accessCode ? masks.unmask(accessCode) : undefined;
      }
      return registerUser(data, id || 0);
    },
    {
      onSuccess(data) {
        setAccessToken(data.token);
        updateProfile({});
        navigate('/');
      },
      onError(err: any) {
        let errorMessage: any = 'Revise as informações do formulário e tente novamente.';

        if (err?.response?.data) {
          const errorData = err?.response?.data;
          errorMessage = Object.keys(errorData)
            .map((key) => `${errorData[key].join(' ')}`)
            .join(', ');
        }

        toast({
          title: 'Oops!',
          description: errorMessage,
          status: 'error',
        });
      },
    }
  );

  useEffect(() => {
    if (registerType === 'cpf' && accessCode) setValue('cpf', accessCode);
  }, [accessCode, registerType, setValue]);

  if (!registerType)
    return (
      <Labeled spacing={7} label="Registrar com:">
        <VStack width={'100%'} spacing={5}>
          {metadata?.moduleexternalcode && (
            <Button onClick={() => setRegisterType('externalCode')}>
              {metadata?.buttonsubscriptionexternalcode || 'Assinatura com código externo'}
            </Button>
          )}
          {cpfModule && (
            <Button onClick={() => setRegisterType('cpf')}>{metadata?.buttoncpfname || 'CPF/CNPJ Vinculado'}</Button>
          )}
          {allowPromocodeLoginMethod && (
            <Button onClick={() => setRegisterType('promocode')}>Código Promocional</Button>
          )}
          {metadata?.subscription !== false && (
            <Button onClick={() => setRegisterType('subscription')}>Assinatura</Button>
          )}

          <TermsOfUseNotification />
        </VStack>
      </Labeled>
    );

  if (!accessCodeValidated && registerType !== 'subscription')
    return (
      <Labeled spacing={5} label={accessCodeLabel}>
        <VStack spacing={5} width={'100%'}>
          {registerType === 'externalCode' ? (
            <>
              {metadata?.masksubscriptionexternalcode === 'phone' ? (
                <CellphoneInput value={accessCode} onChange={(e) => setAccessCode(e.target.value)} />
              ) : (
                <Input value={accessCode} onChange={(e) => setAccessCode(e.target.value)} />
              )}
            </>
          ) : registerType === 'promocode' ? (
            <Input value={accessCode} onChange={(e) => setAccessCode(e.target.value)} />
          ) : (
            <CPFCNPJInput value={accessCode} onChange={(e) => setAccessCode(e.target.value)} />
          )}
          <Button onClick={() => validatorMutation.mutate()} isLoading={validatorMutation.isLoading}>
            Verificar
          </Button>
          <Link
            onClick={() => {
              setAccessCode('');
              setRegisterType(null);
            }}
            color="#a9a9a9"
            fontSize={'14px'}
            textDecor={'underline'}
          >
            Voltar
          </Link>
        </VStack>
      </Labeled>
    );

  return (
    <form onSubmit={handleSubmit((data) => registerMutation.mutate(data as IUserRegistrationData))}>
      <VStack paddingBottom={'50px'} spacing={4} w={'100%'} align={'flex-start'}>
        {activatedPlan && (
          <VStack
            padding={'20px'}
            align={'flex-start'}
            width={'100%'}
            borderRadius={'10px'}
            backgroundColor="primaryColor"
          >
            <Heading color="white" fontSize={'21px'}>
              Parabéns!
            </Heading>
            <Heading color="white" fontSize={'14px'} fontWeight={400}>
              Seu {registerType === 'cpf' ? 'CPF' : registerType === 'promocode' ? 'Promocode' : 'Código'} garante o
              Combo {activatedPlan.name}.
            </Heading>
          </VStack>
        )}
        <Input {...register('full_name', { required: true })} label="Nome Completo *" />
        <Input {...register('email', { required: true })} label="Email *" />
        <CellphoneInput
          label="Celular"
          value={watch('telephone') || ''}
          disabled={registerType === 'externalCode'}
          onChange={(e) => setValue('telephone', e.target.value)}
        />
        <CPFCNPJInput
          value={watch('cpf') || ''}
          onChange={(e) => setValue('cpf', e.target.value)}
          disabled={registerType === 'cpf'}
        />
        <PasswordInput {...register('password', { required: true })} label="Senha *" />
        <UnorderedList textAlign="left" paddingLeft="2%" fontSize="8pt" opacity="0.5">
          <li>Pelo menos 8 caracteres.</li>
          <li>Não ser uma senha comum. Ex: senha123.</li>
          <li>Ter pelo menos uma letra.</li>
        </UnorderedList>
        <Checkbox isChecked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} margin="20px 0px">
          Estou de acordo com os <ExternalLink to="/legal/termos-de-uso">Termo de Uso</ExternalLink>
        </Checkbox>
        <Button isDisabled={!acceptedTerms} isLoading={registerMutation.isLoading} type="submit">
          Registrar
        </Button>
        <Link
          color="#a9a9a9"
          alignSelf={'center'}
          fontSize={'14px'}
          textDecor={'underline'}
          onClick={() => {
            setRegisterType(null);
            setAccessCode('');
            setAccessCodeValidated(false);
            setActivatedPlan(null);
          }}
        >
          Voltar
        </Link>
      </VStack>
      <ModalCPF
        cnpj={accessCode}
        isOpen={modalCpf.isOpen}
        onClose={(cpf) => {
          setValue('cpf', cpf);
          modalCpf.onClose();
        }}
      />
    </form>
  );
}
